/**
 * La marque du cabinet, appliquée à l'écran.
 *
 * La scène visée : une dirigeante ouvre le lien que son cabinet lui a envoyé,
 * et la page porte le nom et les couleurs de CE cabinet — avant même qu'elle ne
 * se connecte. La marque est donc chargée au démarrage, sur une route ouverte,
 * et déduite du seul nom d'hôte que le navigateur a demandé.
 *
 * Trois choix, et leurs raisons
 * -----------------------------
 * **Rien n'est demandé au client.** Pas de paramètre `?cabinet=`, pas d'en-tête.
 * Laisser le client choisir la marque qu'on lui sert, c'est lui laisser afficher
 * le cabinet A à l'utilisateur du cabinet B — une page de connexion crédible
 * pour récolter des identifiants.
 *
 * **Les couleurs sont posées avant le premier rendu utile**, sur `<html>`, pour
 * éviter le clignotement où l'écran s'affiche en bleu maison puis bascule aux
 * couleurs du cabinet. La valeur est mise en cache de session pour que le
 * deuxième chargement n'ait rien à attendre.
 *
 * **L'échec est silencieux.** API injoignable, réponse illisible : on garde le
 * thème maison. Un utilisateur ne doit jamais rester devant une page blanche
 * parce qu'un logo n'a pas pu être résolu.
 */

export interface ContexteDeMarque {
  nomAffiche: string;
  variablesCss: Record<string, string>;
  logoUrl: string | null;
  piedDePage: string | null;
  parDefaut: boolean;
  contrasteConforme: boolean;
}

export const MARQUE_MAISON: ContexteDeMarque = {
  nomAffiche: "Innov'Paie",
  variablesCss: {
    '--marque-accent': '#024888',
    '--marque-sur-accent': '#ffffff',
    '--marque-fond': '#ffffff',
    '--marque-encre': '#1c1a17',
    '--marque-encre-douce': '#55504a',
    '--marque-trait': '#8a8f98',
  },
  logoUrl: null,
  piedDePage: null,
  parDefaut: true,
  contrasteConforme: true,
};

const CLE_CACHE = 'marque:contexte';

/** Seules ces variables sont acceptées : une clé inconnue n'est pas posée. */
const VARIABLES_ATTENDUES = new Set(Object.keys(MARQUE_MAISON.variablesCss));

/**
 * Une couleur, ou rien.
 *
 * La valeur vient du réseau et va dans `style.setProperty`. Une chaîne comme
 * `red;--autre:...` y serait interprétée. On n'assainit pas : on refuse ce qui
 * n'est pas exactement une couleur hexadécimale.
 */
function couleurValide(valeur: unknown): valeur is string {
  return typeof valeur === 'string' && /^#[0-9a-fA-F]{6}$/.test(valeur.trim());
}

/**
 * La marque en service, et ceux qui veulent le savoir.
 *
 * Un objet de module plutôt qu'un contexte React : la marque est résolue une
 * fois au démarrage et ne bouge plus. Imposer un fournisseur autour de l'arbre
 * obligerait chaque écran à savoir qu'une marque existe — exactement ce qu'une
 * marque blanche doit éviter.
 */
let enService: ContexteDeMarque = MARQUE_MAISON;
const abonnes = new Set<() => void>();

export function marqueCourante(): ContexteDeMarque {
  return enService;
}

export function abonnerALaMarque(rappel: () => void): () => void {
  abonnes.add(rappel);
  return () => abonnes.delete(rappel);
}

/** Mélange une couleur vers le blanc (t<0) ou vers le noir (t>0). */
function nuance(hex: string, t: number): string {
  const n = parseInt(hex.slice(1), 16);
  const canaux = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const cible = t >= 0 ? 0 : 255;
    const k = Math.abs(t);
    return Math.round(c + (cible - c) * k);
  });
  return `#${canaux.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Les jetons de thème Tailwind, dérivés de l'accent.
 *
 * Sans cela, la marque s'arrête à l'écran de connexion : le reste de
 * l'application est écrit en `bg-brand-600`, `text-primary-700`… et resterait
 * orange et bleu maison pour un cabinet vert. Le parcours du 16/08 l'a montré
 * en image — une barre latérale orange sous une marque verte.
 *
 * Réécrire des centaines de classes n'était pas la bonne réponse : en Tailwind
 * v4, `@theme` produit de vraies variables CSS. On redéfinit donc l'échelle
 * elle-même, et toute l'application suit sans qu'aucun écran n'ait à savoir
 * qu'une marque existe.
 *
 * Les gris (`neutral-*`) ne sont PAS dérivés : ils portent la lisibilité du
 * texte courant, et une charte fantaisiste ne doit pas pouvoir la dégrader.
 */
function jetonsDerives(accent: string): Record<string, string> {
  const echelle: [string, number][] = [
    ['50', -0.94],
    ['100', -0.86],
    ['200', -0.7],
    ['300', -0.5],
    ['400', -0.26],
    ['500', -0.1],
    ['600', 0],
    ['700', 0.16],
    ['800', 0.3],
    ['900', 0.44],
    ['950', 0.6],
  ];
  const jetons: Record<string, string> = {};
  for (const [niveau, t] of echelle) {
    const couleur = nuance(accent, t);
    jetons[`--color-brand-${niveau}`] = couleur;
    jetons[`--color-primary-${niveau}`] = couleur;
  }
  return jetons;
}

export function appliquerLaMarque(marque: ContexteDeMarque): void {
  const racine = document.documentElement;
  for (const [cle, valeur] of Object.entries(marque.variablesCss)) {
    if (!VARIABLES_ATTENDUES.has(cle)) continue;
    if (!couleurValide(valeur)) continue;
    racine.style.setProperty(cle, valeur.trim());
  }

  // Sous marque maison, on ne touche à rien : l'application garde exactement
  // les couleurs qu'elle a toujours eues.
  if (!marque.parDefaut) {
    const accent = marque.variablesCss['--marque-accent'];
    if (couleurValide(accent)) {
      for (const [cle, valeur] of Object.entries(jetonsDerives(accent.trim()))) {
        racine.style.setProperty(cle, valeur);
      }
    }
  }
  // Sert aux captures et au débogage : on doit pouvoir dire, en regardant une
  // page, quelle marque a été résolue.
  racine.dataset.marque = marque.parDefaut ? 'maison' : 'cabinet';

  // Le nom de l'onglet fait partie de la marque : une entreprise qui épingle
  // l'onglet de son cabinet ne doit pas y lire le nom de la plateforme.
  if (!marque.parDefaut) document.title = marque.nomAffiche;

  enService = marque;
  for (const rappel of abonnes) rappel();
}

function lireLeCache(): ContexteDeMarque | null {
  try {
    const brut = sessionStorage.getItem(CLE_CACHE);
    if (!brut) return null;
    const lu = JSON.parse(brut) as ContexteDeMarque;
    return lu && typeof lu.nomAffiche === 'string' ? lu : null;
  } catch {
    return null;
  }
}

/**
 * Charge la marque et l'applique. Rend celle qui a été appliquée.
 *
 * Le cache de session sert d'abord, l'appel réseau le rafraîchit ensuite : la
 * page ne clignote pas, et une marque révoquée redescend au chargement suivant.
 */
export async function chargerLaMarque(
  /** `APP_CONFIG.api.baseUrl` — il porte déjà le préfixe `/api/v1`. */
  baseUrl: string,
): Promise<ContexteDeMarque> {
  const enCache = lireLeCache();
  if (enCache) appliquerLaMarque(enCache);

  try {
    const reponse = await fetch(`${baseUrl}/branding/context`, {
      // Route publique : pas de cookie, donc pas de préparation CORS créditée.
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
    if (!reponse.ok) throw new Error(String(reponse.status));
    // L'API enveloppe ses réponses dans `{ statusCode, data }`
    // (`TransformInterceptor`). On accepte les deux formes : si l'enveloppe
    // disparaît un jour, la marque ne tombe pas silencieusement au repli.
    const brut = (await reponse.json()) as
      | ContexteDeMarque
      | { data: ContexteDeMarque };
    const marque = (
      brut && typeof brut === 'object' && 'data' in brut ? brut.data : brut
    ) as ContexteDeMarque;
    if (!marque || typeof marque.nomAffiche !== 'string') {
      throw new Error('réponse illisible');
    }
    appliquerLaMarque(marque);
    try {
      sessionStorage.setItem(CLE_CACHE, JSON.stringify(marque));
    } catch {
      /* stockage plein ou refusé : sans conséquence */
    }
    return marque;
  } catch {
    const repli = enCache ?? MARQUE_MAISON;
    appliquerLaMarque(repli);
    return repli;
  }
}
